import { useAdminSelectQuery } from "@crowdcoursing/api/graphql";
import {
  type BubbleOnboardingResponseDto,
  type OnboardingStatusDto,
  type SchoolOnboardingRowDto,
  useLoginWhoAmI,
  useOnboardingGetBubble,
} from "@crowdcoursing/api/openapi";
import { ArrowBack, ArrowDropDown, Business as BusinessIcon } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useFeature } from "~hooks/useFeature";
import { type ColumnData, type RowData } from "~types/datagrid";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";
import { getAdminBubbleIds } from "~utils/getAdminBubbleIds";
import { makeBubbleAvatarUrl } from "~utils/url";

import { canEditOnboardingTarget } from "./onboardingAccess";
import { OnboardingMetricsList } from "./OnboardingMetricsList";
import { OnboardingStatusChip } from "./OnboardingStatusChip";
import { OnboardingSummaryCards, type OnboardingSummaryStat } from "./OnboardingSummaryCards";
import { useOnboardingMutations } from "./useOnboardingMutations";

function districtSummaryStats(
  bubble: BubbleOnboardingResponseDto,
  bubbleId: string,
  showResumes: boolean,
): OnboardingSummaryStat[] {
  const schools = bubble.schools ?? [];
  return [
    { label: "Schools", testId: `onboardingSummary-schools-${bubbleId}`, value: bubble.schoolCount ?? schools.length },
    {
      label: "Schools not launched",
      testId: `onboardingSummary-schoolsNotLaunched-${bubbleId}`,
      value: schools.filter((school) => school.onboardingStatus?.name !== "Launched").length,
    },
    showResumes
      ? {
          label: "Searchable Candidates",
          testId: `onboardingSummary-searchable-${bubbleId}`,
          value: bubble.metrics?.searchable ?? 0,
        }
      : {
          label: "Students",
          testId: `onboardingSummary-students-${bubbleId}`,
          value: bubble.metrics?.students ?? 0,
        },
  ];
}

function schoolSummaryStats(
  bubble: BubbleOnboardingResponseDto,
  bubbleId: string,
  showResumes: boolean,
): OnboardingSummaryStat[] {
  const students = {
    label: "Students",
    testId: `onboardingSummary-students-${bubbleId}`,
    value: bubble.metrics?.students ?? 0,
  };
  if (!showResumes) {
    return [
      students,
      {
        label: "Total users",
        testId: `onboardingSummary-totalUsers-${bubbleId}`,
        value: bubble.metrics?.totalUsers ?? 0,
      },
      { label: "Admins", testId: `onboardingSummary-admins-${bubbleId}`, value: bubble.metrics?.admins ?? 0 },
    ];
  }
  return [
    students,
    {
      label: "Incomplete candidates",
      testId: `onboardingSummary-incomplete-${bubbleId}`,
      value: bubble.metrics?.candidateIncomplete ?? 0,
    },
    {
      label: "Searchable Candidates",
      testId: `onboardingSummary-searchable-${bubbleId}`,
      value: bubble.metrics?.searchable ?? 0,
    },
  ];
}

export function BubbleOnboardingView({
  bubbleId,
  onBack,
  parentBubbleId,
}: {
  bubbleId: string;
  onBack?: () => void;
  parentBubbleId?: string;
}) {
  const query = useOnboardingGetBubble({ pathParams: { bubbleId } });

  if (query.status === "pending") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (query.status === "error" || !query.data) {
    return <Alert severity="error">Onboarding details could not be loaded.</Alert>;
  }

  return (
    <BubbleOnboardingBody
      bubble={query.data}
      bubbleId={bubbleId}
      parentBubbleId={parentBubbleId}
      onBack={onBack}
    />
  );
}

function BubbleOnboardingBody({
  bubble,
  bubbleId,
  onBack,
  parentBubbleId,
}: {
  bubble: BubbleOnboardingResponseDto;
  bubbleId: string;
  onBack?: () => void;
  parentBubbleId?: string;
}) {
  const { data: whoAmI } = useLoginWhoAmI({});
  const { data: adminSelect } = useAdminSelectQuery();
  const access = canEditOnboardingTarget({
    adminBubbleIds: getAdminBubbleIds(whoAmI?.userDto?.bubbleRoles),
    adminDistricts: adminSelect?.mySchoolDistricts ?? [],
    bubbleType: bubble.bubbleType,
    isSiteAdmin: whoAmI?.isSiteAdmin ?? false,
    schoolDistrictId: bubble.schoolDistrictId,
  });
  const mutations = useOnboardingMutations(bubbleId, parentBubbleId);
  const showResumes = useFeature("showResumes");
  const isDistrict = bubble.bubbleType === 3;
  const version = bubble.version ?? 0;
  const summary = useMemo(
    () =>
      isDistrict
        ? districtSummaryStats(bubble, bubbleId, showResumes)
        : schoolSummaryStats(bubble, bubbleId, showResumes),
    [bubble, bubbleId, isDistrict, showResumes],
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {onBack ? (
          <Button startIcon={<ArrowBack />} onClick={onBack}>
            Back
          </Button>
        ) : null}
        <Avatar
          slotProps={{
            img: {
              "aria-label": `${bubble.name ?? "Organization"} ${isDistrict ? "district" : "school"} avatar`,
            },
          }}
          src={makeBubbleAvatarUrl(bubbleId)}
          sx={{ height: 40, width: 40 }}
        >
          <BusinessIcon />
        </Avatar>
        <Typography variant="h6">{bubble.name}</Typography>
      </Stack>

      <OnboardingSummaryCards stats={summary} />

      <StatusSection
        access={access.status}
        kindLabel="Onboarding status"
        pending={mutations.setStatus.isPending}
        selectedId={bubble.onboardingStatus?.id}
        statuses={bubble.availableOnboardingStatuses ?? []}
        testId={`onboardingStatusSelect-${bubbleId}`}
        version={version}
        onChange={(statusDefinitionId) => {
          mutations.setStatus.mutate({ body: { statusDefinitionId, version }, pathParams: { bubbleId } });
        }}
      />

      {isDistrict ? (
        <StatusSection
          access={access.dpa}
          kindLabel="DPA Status"
          pending={mutations.setDpa.isPending}
          selectedId={bubble.dpaStatus?.id}
          statuses={bubble.availableDpaStatuses ?? []}
          testId={`dpaStatusSelect-${bubbleId}`}
          version={version}
          onChange={(statusDefinitionId) => {
            mutations.setDpa.mutate({ body: { statusDefinitionId, version }, pathParams: { bubbleId } });
          }}
        />
      ) : null}

      {!access.status ? (
        <Typography color="text.secondary" variant="body2">
          {isDistrict
            ? "Site Admin sets this district's status and DPA. District admins set statuses for schools."
            : "School admins can read this status. A district admin or Site Admin changes it."}
        </Typography>
      ) : null}

      <Box>
        <Typography sx={{ mb: 1 }} variant="subtitle1">
          People
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
          {showResumes
            ? "Counts are distinct users. Student and Candidate overlap, so Both is counted in both role totals. Click a count to open User Management."
            : "Counts are distinct users. Click a count to open User Management."}
        </Typography>
        <OnboardingMetricsList bubbleId={bubbleId} metrics={bubble.metrics} />
      </Box>

      {isDistrict ? <DistrictSchools bubble={bubble} showResumes={showResumes} /> : null}
    </Stack>
  );
}

function StatusSection({
  access,
  kindLabel,
  onChange,
  pending,
  selectedId,
  statuses,
  testId,
  version,
}: {
  access: boolean;
  kindLabel: string;
  onChange: (statusDefinitionId: string) => void;
  pending: boolean;
  selectedId: string | undefined;
  statuses: OnboardingStatusDto[];
  testId: string;
  version: number;
}) {
  const selected = statuses.find((status) => status.id === selectedId) ?? statuses[0];
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">{kindLabel}</Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        {access ? (
          <>
            <Chip
              clickable
              aria-haspopup="menu"
              aria-label={`Change ${kindLabel}`}
              data-testid={testId}
              disabled={pending}
              label={
                <Box sx={{ alignItems: "center", display: "flex" }}>
                  {selected?.name || "Not started"}
                  <ArrowDropDown fontSize="small" />
                </Box>
              }
              sx={{
                bgcolor: selected?.backgroundColor || "action.hover",
                color: selected?.textColor || "text.primary",
                fontWeight: 600,
              }}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
            />
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
              {statuses.map((status) => (
                <MenuItem
                  key={status.id ?? status.name}
                  selected={status.id === selectedId}
                  onClick={() => {
                    closeMenu();
                    if (status.id && status.id !== selectedId) {
                      onChange(status.id);
                    }
                  }}
                >
                  {status.name}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <OnboardingStatusChip status={selected} testId={`${testId}-chip`} />
        )}
      </Stack>
      {access && version === 0 ? (
        <Typography color="text.secondary" variant="caption">
          No saved row yet. The first change stores this status.
        </Typography>
      ) : null}
    </Stack>
  );
}

interface SchoolGridRow {
  bubbleId: string;
  name: string;
  onboardingStatus?: OnboardingStatusDto;
  searchable: number;
  studentOnly: number;
}

type SchoolGridData = RowData<SchoolGridRow>;

function SchoolOnboardingGrid({ schools, showResumes }: { schools: SchoolOnboardingRowDto[]; showResumes: boolean }) {
  const rows = useMemo(
    (): SchoolGridData[] =>
      schools.flatMap((school) => {
        const bubbleId = school.bubbleId;
        if (!bubbleId) {
          return [];
        }
        return [
          {
            bubbleId,
            name: school.name ?? "School",
            onboardingStatus: school.onboardingStatus,
            rowId: bubbleId,
            searchable: school.metrics?.searchable ?? 0,
            studentOnly: school.metrics?.studentOnly ?? 0,
          },
        ];
      }),
    [schools],
  );

  const columns = useMemo((): ColumnData<SchoolGridRow>[] => {
    return [
      {
        field: "name",
        flex: 1.4,
        headerName: "School",
        minWidth: 240,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0, py: 0.5 }}>
            <Avatar
              slotProps={{ img: { "aria-label": `${row.name} school avatar` } }}
              src={makeBubbleAvatarUrl(row.bubbleId)}
              sx={{ height: 32, width: 32 }}
            >
              <BusinessIcon fontSize="small" />
            </Avatar>
            <MuiLink
              component={RouterLink}
              data-testid={`onboardingSchool-${row.bubbleId}`}
              sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
              to={`/admin?adminSelect=${row.bubbleId}&adminTab=onboarding`}
              underline="hover"
              variant="body2"
              onClick={(event) => event.stopPropagation()}
            >
              {row.name}
            </MuiLink>
          </Stack>
        ),
      },
      {
        field: "onboardingStatus",
        headerName: "Status",
        minWidth: 150,
        renderCell: ({ row }) => (
          <OnboardingStatusChip status={row.onboardingStatus} testId={`onboardingSchoolStatus-${row.bubbleId}`} />
        ),
        sortable: false,
      },
      { align: "right", field: "studentOnly", headerAlign: "right", headerName: "Student-only", minWidth: 130 },
      {
        align: "right",
        field: "searchable",
        headerAlign: "right",
        headerName: "Searchable Candidates",
        minWidth: 190,
      },
    ].filter((column) => showResumes || (column.field !== "studentOnly" && column.field !== "searchable"));
  }, [showResumes]);

  return (
    <DataGrid<SchoolGridRow>
      autoHeight
      columns={columns}
      disableRowSelectionOnClick
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      rows={rows}
      slotProps={{ empty: { message: "No schools in this district yet." } }}
      sx={{ "& .MuiDataGrid-cell": { alignItems: "center", display: "flex" } }}
    />
  );
}

function DistrictSchools({ bubble, showResumes }: { bubble: BubbleOnboardingResponseDto; showResumes: boolean }) {
  const schools = useMemo(() => bubble.schools ?? [], [bubble.schools]);

  return (
    <Box>
      <Typography variant="subtitle1">Schools</Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
        {bubble.schoolCount ?? 0} schools, {bubble.schoolsWithUsers ?? 0} with users.
      </Typography>
      <SchoolOnboardingGrid schools={schools} showResumes={showResumes} />
    </Box>
  );
}
